import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';

import { ImageZoom } from '../ImageZoom';

type ImagePreviewerProps = {
  imageStyle?: string;
  imageURL: string | null;
  containerStyle?: string;
  showOutline?: boolean;
  allowZoom?: boolean;
  zoomImageURL?: string;
};

const ImageErrorIcon = () => {
  return (
    <svg
      className="pointer-events-none"
      xmlns="http://www.w3.org/2000/svg"
      width="68"
      height="60"
      viewBox="0 0 68 60"
      data-testid="image-error-icon"
    >
      <path
        className="fill-red-50"
        d="M11 11h53a4 4 0 014 4v37a6 6 0 01-6 6H13a4 4 0 01-4-4V13a2 2 0 012-2z"
      />
      <g className="fill-red-600" fillRule="nonzero">
        <path d="M17.421 6l1.155 2H8a2 2 0 00-1.995 1.85L6 10v40a2 2 0 001.85 1.995L8 52h35.98l1.154 2H8a4 4 0 01-4-4V10a4 4 0 014-4h9.421zM60 6a4 4 0 014 4v40a4 4 0 01-4 4h-7.937l-1.154-2H60a2 2 0 001.995-1.85L62 50V10a2 2 0 00-1.85-1.995L60 8H25.505L24.35 6H60z" />
        <path d="M19 15a6 6 0 110 12 6 6 0 010-12zm0 2a4 4 0 100 8 4 4 0 000-8zm5.113 15.21l.094.083 5.293 5.292 3.903-3.904 1.036 1.794-4.232 4.232a1 1 0 01-1.32.083l-.094-.083-5.293-5.292-10.293 10.292a1 1 0 01-1.32.083l-.094-.083a1 1 0 01-.083-1.32l.083-.094 11-11a1 1 0 011.32-.083zm22.5-10.5l.094.083 9.5 9.5a1 1 0 01-1.32 1.497l-.094-.083L46 23.915l-7.168 7.167-1.036-1.794 7.497-7.495a1 1 0 011.32-.083zM17.5.134a1 1 0 011.302.268l.064.098 33 57.158a1 1 0 01-1.668 1.097l-.064-.097-33-57.158A1 1 0 0117.5.134z" />
      </g>
    </svg>
  );
};

const ImagePreviewer: React.FC<ImagePreviewerProps> = ({
  imageStyle = '',
  imageURL,
  containerStyle = '',
  showOutline = false,
  allowZoom,
  zoomImageURL,
}: React.PropsWithChildren<ImagePreviewerProps>) => {
  const [didImageError, setDidImageError] = React.useState(false);

  const imageClassName = cx(imageStyle, 'max-w-full');

  const handleOnImageError = () => {
    setDidImageError(true);
  };
  const handleOnImageLoad = () => {
    setDidImageError(false);
  };
  const imgDivStyle = React.useMemo(() => {
    return cx({
      'flex justify-center items-center': didImageError,
      'rounded-1 shadow-outline-px inset-0 text-grey-400 w-64 h-64':
        didImageError && showOutline,
    });
  }, [didImageError, showOutline]);

  return (
    <div className={`${imgDivStyle} ${containerStyle}`}>
      {didImageError ? (
        <ImageErrorIcon />
      ) : allowZoom ? (
        <ImageZoom
          zoomSrc={zoomImageURL || imageURL}
          openLabel={translate('Zoom')}
          closeLabel={translate('Close')}
        >
          <img
            data-testid="image-previewer"
            className={imageClassName}
            src={imageURL}
            alt=""
            onLoad={handleOnImageLoad}
            onError={handleOnImageError}
          />
        </ImageZoom>
      ) : (
        <img
          data-testid="image-previewer"
          className={imageClassName}
          src={imageURL}
          alt=""
          onLoad={handleOnImageLoad}
          onError={handleOnImageError}
        />
      )}
    </div>
  );
};

export default ImagePreviewer;
